import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, ShoppingBasket } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useStore, lineTid } from "@/context/StoreContext";
import { imgUrl, inr } from "@/lib/api";
import { QtyStepper } from "@/components/store/Bits";
import { PolicyNote } from "@/components/store/PolicyNote";
import { FeeLines, MinOrderNotice } from "@/components/store/DeliveryRules";

export const CartSheet = () => {
  const { cart, setQty, shortBy, cartOpen, setCartOpen, refreshCart, config, area } = useStore();
  const nav = useNavigate();
  useEffect(() => { if (cartOpen) refreshCart().catch(() => {}); }, [cartOpen, refreshCart]);
  const closed = config && !config.ordering_open;

  return (
    <Sheet open={cartOpen} onOpenChange={setCartOpen}>
      <SheetContent className="bg-white w-full sm:max-w-md flex flex-col p-0" data-testid="cart-sheet">
        <SheetHeader className="px-6 pt-6">
          <SheetTitle className="font-display text-3xl text-primary">Your basket</SheetTitle>
          <SheetDescription>Delivering {cart.some((i) => i.needs_prep) ? "tomorrow" : "today"} to {area ? `${area.pincode} · ${area.name}` : "—"}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 overflow-auto px-6 py-4 space-y-4">
          {cart.length === 0 && (
            <div className="text-center py-16 text-stone-500" data-testid="cart-empty">
              <ShoppingBasket className="w-10 h-10 mx-auto mb-3 text-stone-300" strokeWidth={1.5} />
              Your basket is empty.
            </div>
          )}
          {cart.map((i) => (
            <div key={i.key} className="flex gap-3 items-center" data-testid={`cart-item-${lineTid(i)}`}>
              <img src={imgUrl(i.image)} alt={i.name} className="w-16 h-16 rounded-xl object-cover" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-primary truncate">{i.name}{i.cut && <span className="ml-1.5 text-[11px] font-bold text-accent" data-testid={`cart-cut-${lineTid(i)}`}>{i.cut}</span>}</p>
                <p className="text-xs text-stone-500">{i.unit} · {inr(i.price)}</p>
                <p className="text-sm font-bold mt-0.5">{inr(i.price * i.qty)}</p>
              </div>
              <QtyStepper qty={i.qty} max={i.available_qty} onChange={(q) => setQty(i.key, q)} testId={`cart-qty-${lineTid(i)}`} />
              <button onClick={() => setQty(i.key, 0)} className="p-2 text-stone-400 hover:text-accent transition-colors" data-testid={`cart-remove-${lineTid(i)}`}>
                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>
          ))}
        </div>
        {cart.length > 0 && (
          <div className="border-t border-stone-100 px-6 py-5 space-y-4 bg-[#F9F8F4]">
            <PolicyNote compact />
            <FeeLines />
            <MinOrderNotice />
            <p className="text-xs text-stone-500">Final price is locked when you place the order.</p>
            {closed && <p className="text-sm text-accent font-semibold" data-testid="cart-closed-msg">{config.closed_reason}</p>}
            <button disabled={closed || shortBy > 0} onClick={() => { setCartOpen(false); nav("/checkout"); }} data-testid="cart-checkout-btn"
              className="w-full h-12 rounded-full bg-primary text-white font-bold disabled:opacity-40 active:scale-95 transition-transform">
              Checkout
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
